import Layout from '@/components/layout/layout'
import { Box, Button, Flex, Grid, Heading, Image, Text } from '@chakra-ui/react'
import Icon from '@/components/shared/icons'
import { Line } from 'react-chartjs-2'
import {
	Chart as ChartJS,
	LineElement,
	CategoryScale,
	LinearScale,
	PointElement,
	Legend,
	Tooltip,
} from 'chart.js'
import JwtPayload from '@/entities/api/jwt'
import { AppDispatch, RootState } from '@/store/store'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useState } from 'react'
import { SaleEntity } from '@/entities/api/sales/sales'
import { fetchSales } from '@/services/api/sales/sales'
import Toast from '@/components/elements/toast'
import {
	fetchOrdersCountByStatus,
	getLatestContacts,
	getLatestCustomers,
} from '@/services/api/stadicts/stadicts'
import { sessionRefrestUpdate } from '@/store/features/state'
import ContactEntity from '@/entities/api/users/contact'
import CustomersEntity from '@/entities/api/customers/customers'
import { API_URL } from '@/services/api/keys'
import { useNavigate } from 'react-router-dom'
import {
	formatDate,
	formatDateYYYYMMDD,
	formatToMMDD,
	nowBogota,
	parseDateBogota,
} from '@/utils/date'

ChartJS.register(
	LineElement,
	CategoryScale,
	LinearScale,
	PointElement,
	Legend,
	Tooltip
)

interface OrderStatsData {
	new: number
	pending: number
	processing: number
	on_the_way: number
	rejected: number
	delivered: number
	refunded: number
}

const Statistics = ({ data }: { data: JwtPayload }) => {
	const token = useSelector((state: RootState) => state.auth.token)
	const [sales, setSales] = useState<SaleEntity[]>([])
	const [ordersCount, setOrdersCount] = useState<OrderStatsData>({
		new: 0,
		pending: 0,
		processing: 0,
		on_the_way: 0,
		rejected: 0,
		delivered: 0,
		refunded: 0,
	})
	const [isLoading, setIsLoading] = useState(false)
	const [fromDate, setFromDate] = useState('')
	const [toDate, setToDate] = useState('')
	const [contacts, setContacts] = useState<ContactEntity[]>([])
	const [customers, setCustomers] = useState<CustomersEntity[]>([])
	const dispatch = useDispatch<AppDispatch>()
	const navigation = useNavigate()

	useEffect(() => {
		const now = nowBogota()
		const lastMonth = new Date(now)
		lastMonth.setMonth(now.getMonth() - 1)

		setFromDate(formatDateYYYYMMDD(lastMonth))
		setToDate(formatDateYYYYMMDD(now))
	}, [])

	useEffect(() => {
		setIsLoading(true)
		const loadSales = async () => {
			try {
				const fromDateTime = parseDateBogota(fromDate, true)
				const toDateTime = parseDateBogota(toDate, false)

				const response = await fetchSales(
					token ?? '',
					formatDateYYYYMMDD(fromDateTime),
					formatDateYYYYMMDD(toDateTime)
				)

				if (response.status === 200) {
					setSales(response.data)
				} else {
					throw new Error('Error al cargar las ventas')
				}
			} catch (error) {
				Toast({
					type: 'error',
					message: 'No se pudieron cargar las ventas',
				})
			}
		}

		const loadOrderCount = async () => {
			try {
				const response = await fetchOrdersCountByStatus(token ?? '')

				if (response?.status === 401) {
					dispatch(sessionRefrestUpdate())
					return
				}

				setOrdersCount(
					response.data.data ?? {
						new: 0,
						pending: 0,
						processing: 0,
						on_the_way: 0,
						rejected: 0,
						delivered: 0,
						refunded: 0,
					}
				)
			} catch (error) {
				Toast({
					type: 'error',
					message: 'No se pudieron cargar las estadísticas de órdenes',
				})
			}
		}

		const lastContacts = async () => {
			try {
				const response = await getLatestContacts(token ?? '')

				if (response?.status === 401) {
					dispatch(sessionRefrestUpdate())
					return
				}

				setContacts(response.data ?? [])
			} catch (error) {
				Toast({
					type: 'error',
					message: 'No se pudieron cargar los contactos',
				})
			}
		}

		const lastCustomers = async () => {
			try {
				const response = await getLatestCustomers(token ?? '')

				if (response?.status === 401) {
					dispatch(sessionRefrestUpdate())
					return
				}

				setCustomers(response.data ?? [])
			} catch (error) {
				Toast({
					type: 'error',
					message: 'No se pudieron cargar los clientes',
				})
			}
		}

		if (toDate && fromDate) {
			loadSales()
		}
		loadOrderCount()
		lastContacts()
		lastCustomers()

		setIsLoading(false)
	}, [toDate, fromDate, token, dispatch])

	const getNewOrders = () => ordersCount.new + ordersCount.pending
	const getProcessingOrders = () =>
		ordersCount.processing + ordersCount.on_the_way
	const getDeliveredOrders = () => ordersCount.delivered

	const processChartData = () => {
		if (!sales.length) {
			return {
				labels: ['Sin datos'],
				datasets: [
					{
						label: 'Ventas Diarias',
						data: [0],
						borderColor: 'rgba(75, 192, 192, 1)',
						backgroundColor: 'rgba(75, 192, 192, 0.2)',
						tension: 0.4,
					},
				],
			}
		}

		const salesByDate = sales.reduce((acc, sale) => {
			const date = formatToMMDD(new Date(sale.createdDate.toString()))
			if (!acc[date]) {
				acc[date] = { count: 0, total: 0 }
			}
			acc[date].count += 1
			acc[date].total += sale.total
			return acc
		}, {} as Record<string, { count: number; total: number }>)

		const sortedDates = Object.keys(salesByDate).sort()
		const salesCounts = sortedDates.map((date) => salesByDate[date].count)
		const salesTotals = sortedDates.map(
			(date) => salesByDate[date].total / 1000
		)

		return {
			labels: sortedDates,
			datasets: [
				{
					label: 'Cantidad de Ventas',
					data: salesCounts,
					borderColor: 'rgba(75, 192, 192, 1)',
					backgroundColor: 'rgba(75, 192, 192, 0.2)',
					tension: 0.4,
					yAxisID: 'y',
				},
				{
					label: 'Monto (Miles COP)',
					data: salesTotals,
					borderColor: 'rgba(153, 102, 255, 1)',
					backgroundColor: 'rgba(153, 102, 255, 0.2)',
					tension: 0.4,
					yAxisID: 'y1',
				},
			],
		}
	}

	const chartData = processChartData()

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		interaction: {
			mode: 'index' as const,
			intersect: false,
		},
		plugins: {
			legend: {
				position: 'top' as const,
				labels: {
					color: 'black',
				},
			},
			tooltip: {
				enabled: true,
				callbacks: {
					label: (context: any) => {
						if (context.datasetIndex === 1) {
							return `${context.dataset.label}: $${(
								context.parsed.y * 1000
							).toLocaleString()} COP`
						}
						return `${context.dataset.label}: ${context.parsed.y}`
					},
				},
			},
		},
		scales: {
			x: {
				ticks: {
					color: 'black',
				},
			},
			y: {
				type: 'linear' as const,
				display: true,
				position: 'left' as const,
				ticks: {
					color: 'black',
				},
				beginAtZero: true,
				title: {
					display: true,
					text: 'Cantidad de Ventas',
					color: 'black',
				},
			},
			y1: {
				type: 'linear' as const,
				display: true,
				position: 'right' as const,
				ticks: {
					color: 'purple',
				},
				beginAtZero: true,
				title: {
					display: true,
					text: 'Monto (Miles COP)',
					color: 'purple',
				},
				grid: {
					drawOnChartArea: false,
				},
			},
		},
	}

	return (
		<Layout data={data}>
			<Grid
				w='full'
				p='6'
				gap='8'
				templateColumns={{
					base: '1fr',
					md: 'repeat(2, 1fr)',
					lg: 'repeat(3, 1fr)',
				}}
			>
				<Box borderRadius='md' boxShadow='sm' p='6' bg='white'>
					<Heading as='h2' fontSize='xl' color='gray.600'>
						Pedidos nuevos
					</Heading>
					<Box
						display='flex'
						w='full'
						alignItems='center'
						justifyContent='end'
						mt='4vh'
						p='6'
						position='relative'
					>
						<Box
							transform='rotate(-20deg)'
							position='absolute'
							bottom='0'
							left='0'
						>
							<Icon icon='basket' size='5x' color='gray' />
						</Box>
						<Heading as='h1' fontSize='6xl' color='black'>
							{getNewOrders()}
						</Heading>
					</Box>
				</Box>
				<Box borderRadius='md' boxShadow='sm' p='6' bg='white'>
					<Heading as='h2' fontSize='xl' color='gray.600'>
						Pedidos en Proceso
					</Heading>
					<Box
						display='flex'
						w='full'
						alignItems='center'
						justifyContent='end'
						mt='4vh'
						p='6'
						position='relative'
					>
						<Box
							transform='rotate(-20deg)'
							position='absolute'
							bottom='0'
							left='0'
						>
							<Icon icon='shipping' size='5x' color='orange' />
						</Box>
						<Heading as='h1' fontSize='6xl' color='black'>
							{getProcessingOrders()}
						</Heading>
					</Box>
				</Box>
				<Box borderRadius='md' boxShadow='sm' p='6' bg='white'>
					<Heading as='h2' fontSize='xl' color='gray.600'>
						Pedidos Entregados
					</Heading>
					<Box
						display='flex'
						w='full'
						alignItems='center'
						justifyContent='end'
						mt='4vh'
						p='6'
						position='relative'
					>
						<Box
							transform='rotate(-20deg)'
							position='absolute'
							bottom='0'
							left='0'
						>
							<Icon icon='check' size='5x' color='green' />
						</Box>
						<Heading as='h1' fontSize='6xl' color='black'>
							{getDeliveredOrders()}
						</Heading>
					</Box>
				</Box>
			</Grid>
			<Flex
				w='full'
				h='700px'
				p='6'
				flexDirection={{ base: 'column', xl: 'row' }}
				justifyContent='space-between'
			>
				<Flex w={{ base: '100%', xl: '38%' }} h='full' flexDirection='column'>
					<Box
						w='full'
						h='50%'
						p='4'
						mb='2'
						bg='white'
						borderRadius='md'
						boxShadow='sm'
					>
						<Heading as='h3' fontSize='lg' color='black' mb='4'>
							Últimos Mensajes de Contacto
						</Heading>
						<Box w='full' mt='2' mb='2' maxH='200px' overflowY='auto'>
							{contacts.length > 0 ? (
								contacts.slice(0, 3).map((contact, index) => (
									<Box key={index} w='full' mb='3'>
										<Box w='full' display='flex' alignItems='center'>
											<Icon icon='user' size='sm' color='gray' />
											<Heading
												as='h4'
												fontSize={{ base: 'sm', md: 'md' }}
												color='black'
												ml={{ base: '2', md: '4' }}
											>
												{contact.fullname} -{' '}
												{formatDate(contact._created ?? '')}
											</Heading>
										</Box>
										<Text fontSize='sm' mt='1' color='gray.600' ml='6'>
											{contact.email}
										</Text>
										<Text fontSize='sm' mt='1' color='gray.800' ml='6'>
											{contact.message}
										</Text>
									</Box>
								))
							) : (
								<Text fontSize='sm' color='gray.500'>
									No hay mensajes recientes
								</Text>
							)}
						</Box>
						<Box w='full' display='flex' justifyContent='end' mt='2'>
							<Button
								p={{ base: '2', md: '4' }}
								fontWeight='900'
								fontSize={{ base: 'sm', md: 'lg' }}
								bg='black'
								borderRadius='xl'
								color='white'
								_hover={{ bg: 'gray.800' }}
								onClick={() => navigation('/users-request')}
							>
								Ver más
							</Button>
						</Box>
					</Box>
					<Box
						w='full'
						h='50%'
						borderRadius='md'
						p='6'
						boxShadow='sm'
						mt='2'
						bg='white'
					>
						<Heading as='h3' fontSize='lg' color='black' mb='4'>
							Últimos Clientes Registrados
						</Heading>
						<Box w='full' mt='2' mb='2' maxH='200px' overflowY='auto'>
							{customers.length > 0 ? (
								customers.slice(0, 4).map((customer, index) => (
									<Box
										key={index}
										w='full'
										mt='2'
										mb='2'
										display='flex'
										alignItems='center'
									>
										<Image
											src={
												customer.images?.[0]?.url
													? `${API_URL}/${customer.images[0].url}`
													: 'https://placehold.co/400'
											}
											w='4vh'
											h='4vh'
											borderRadius='full'
											objectFit='cover'
										/>

										<Box ml='2' flex='1'>
											<Heading
												as='h2'
												fontSize={{ base: 'sm', md: 'md' }}
												color='black'
											>
												{customer.name} {customer.lastname}
											</Heading>
											<Text fontSize='sm' color='gray.600'>
												Se unió el {formatDate(customer._created ?? '')}
											</Text>
										</Box>
									</Box>
								))
							) : (
								<Text fontSize='sm' color='gray.500'>
									No hay clientes recientes
								</Text>
							)}
						</Box>
					</Box>
				</Flex>
				<Box
					w={{ base: 'full', xl: '60%' }}
					h='full'
					bg='white'
					borderRadius='md'
					p='6'
					boxShadow='sm'
					mt={{ base: '4', xl: '0' }}
				>
					<Heading as='h3' fontSize='xl' color='black' mb='2'>
						Estadísticas de Ventas
					</Heading>
					<Box mb='4'>
						<Text fontSize='sm' color='gray.600'>
							Total de ventas: {sales.length} | Monto total: $
							{sales
								.reduce((sum, sale) => sum + sale.total, 0)
								.toLocaleString()}{' '}
							COP
						</Text>
					</Box>
					<Box w='full' overflowX='auto'>
						<Box h='500px' minW='900px'>
							{isLoading ? (
								<Flex justify='center' align='center' h='full'>
									<Text>Cargando datos...</Text>
								</Flex>
							) : (
								<Line data={chartData} options={options} />
							)}
						</Box>
					</Box>
				</Box>
			</Flex>
		</Layout>
	)
}

export default Statistics
